import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { AdminClient, BroadLocation, CrmAssignee, LeadClosedReason, LeadStatus, WatiTemplates } from '@parkupp/core'
import * as moment from 'moment'

interface CrmTemplateParam {
    name: string
    label: string
    type: 'text' | 'date' | 'number'
    prefillFromLead?: 'firstName'
}

interface CrmTemplateOption {
    value: WatiTemplates
    label: string
    parameters: CrmTemplateParam[]
}

@Component({
    selector: 'app-parker-profile-panel',
    templateUrl: './parker-profile-panel.component.html',
    styleUrls: ['./parker-profile-panel.component.scss'],
})
export class ParkerProfilePanelComponent implements OnChanges {
    @Input() lead: AdminClient | null = null
    @Input() crmAssignees: CrmAssignee[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}
    @Input() isSalesManagerMode: boolean = false
    @Input() contactTypes: string[] = []
    @Input() leadStatuses: LeadStatus[] = []

    @Output() closePanel = new EventEmitter<void>()
    @Output() fieldUpdated = new EventEmitter<{ field: string; value: any }>()
    @Output() userResponsibleUpdated = new EventEmitter<string>()
    @Output() emailClicked = new EventEmitter<string>()
    @Output() phoneClicked = new EventEmitter<string>()
    @Output() whatsappClicked = new EventEmitter<string>()
    @Output() sendTemplateRequested = new EventEmitter<{ template: string; parameters: { name: string; value: string }[] }>()
    @Output() starToggled = new EventEmitter<AdminClient>()
    @Output() addressUpdated = new EventEmitter<{ fullAddress: string; latitude: number; longitude: number }>()
    @Output() tagAdded = new EventEmitter<string>()
    @Output() tagRemoved = new EventEmitter<string>()
    @Output() createTagRequested = new EventEmitter<void>()

    activeTab: 'overview' | 'subscriptions' | 'details' | 'sendMessage' = 'overview'
    avatarUrl: string = ''
    initials: string = ''
    avatarColor: string = ''
    daysSinceCreated: number = 0

    broadLocations = Object.values(BroadLocation)
    leadClosedReasons = Object.values(LeadClosedReason)
    LeadStatus = LeadStatus

    selectedTemplate = ''
    templateParamValues: { [name: string]: string } = {}
    crmWhatsappTemplates: CrmTemplateOption[] = [
        {
            value: WatiTemplates.CRM_NO_ANSWER,
            label: 'No Answer Follow-up',
            parameters: [{ name: 'name', label: 'Name', type: 'text', prefillFromLead: 'firstName' }],
        },
        {
            value: WatiTemplates.CRM_GOOGLE_REVIEW,
            label: 'Google Review Request',
            parameters: [],
        },
        {
            value: WatiTemplates.CRM_NEW_SUBSCRIPTION_CONFIRMATION,
            label: 'New Subscription Confirmation',
            parameters: [
                { name: 'name', label: 'Name', type: 'text', prefillFromLead: 'firstName' },
                { name: 'subscription_start_date', label: 'Start date', type: 'date' },
                { name: 'subscription_end_date', label: 'End date', type: 'date' },
                { name: 'baysbooked', label: 'Number of bays', type: 'number' },
                { name: 'parking_name', label: 'Location', type: 'text' },
            ],
        },
    ]

    get selectedTemplateParams(): CrmTemplateParam[] {
        return this.crmWhatsappTemplates.find((t) => t.value === this.selectedTemplate)?.parameters || []
    }

    onTemplateChange(): void {
        this.templateParamValues = {}
        for (const param of this.selectedTemplateParams) {
            if (param.prefillFromLead === 'firstName') {
                this.templateParamValues[param.name] = this.lead?.firstName || ''
            } else {
                this.templateParamValues[param.name] = ''
            }
        }
    }

    get canSendTemplate(): boolean {
        if (!this.selectedTemplate) return false
        return this.selectedTemplateParams.every((p) => (this.templateParamValues[p.name] || '').trim() !== '')
    }

    emitSendTemplate(): void {
        if (!this.canSendTemplate) return
        const parameters = this.selectedTemplateParams.map((param) => {
            let value = this.templateParamValues[param.name] || ''
            if (param.type === 'date' && value) {
                value = moment(value, 'YYYY-MM-DD').format('D MMMM YYYY')
            }
            return { name: param.name, value }
        })
        this.sendTemplateRequested.emit({ template: this.selectedTemplate, parameters })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['lead'] && this.lead) {
            this.computeAvatar()
            this.computeDays()
        }
    }

    private computeAvatar(): void {
        if (!this.lead) return

        if ((this.lead as any).gravatarUrl) {
            this.avatarUrl = (this.lead as any).gravatarUrl
        } else if (this.lead.profileImage?.originalUrl) {
            this.avatarUrl = this.lead.profileImage.mediumUrl || this.lead.profileImage.originalUrl
        } else {
            this.avatarUrl = ''
        }

        const first = (this.lead.firstName || '?')[0].toUpperCase()
        const last = (this.lead.lastName || '')[0]?.toUpperCase() || ''
        this.initials = first + last

        const str = (this.lead.firstName || '') + (this.lead.lastName || '')
        const hash = str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        const colors = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6', '#8b5cf6']
        this.avatarColor = colors[hash % colors.length]
    }

    onAvatarError(): void {
        this.avatarUrl = ''
    }

    private computeDays(): void {
        if (!this.lead?.createdAt) {
            this.daysSinceCreated = 0
            return
        }
        const created = this.lead.createdAt.toDate ? this.lead.createdAt.toDate() : new Date(this.lead.createdAt as any)
        this.daysSinceCreated = Math.floor((new Date().getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
    }

    getAssigneeName(uid: string | undefined): string {
        if (!uid) return 'Unassigned'
        return this.crmAssigneeMap[uid]?.displayName || 'Unknown'
    }

    getAssigneeColor(uid: string | undefined): string {
        if (!uid) return '#6c757d'
        return this.crmAssigneeMap[uid]?.badgeColor || '#6c757d'
    }

    getSubscriptionSummaries(): any[] {
        const summaries = (this.lead as any)?.subscriptionSummaries
        if (!summaries) return []
        return Object.entries(summaries)
            .map(([key, value]: [string, any]) => ({
                key,
                status: value.status,
                startDate: value.startDate,
                endDate: value.endDate,
                period: value.period,
                baysBooked: value.baysBooked,
                createdAt: value.createdAt,
            }))
            .sort((a, b) => {
                const aTime = a.createdAt?.seconds || 0
                const bTime = b.createdAt?.seconds || 0
                return bTime - aTime
            })
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'Active':
                return 'sub-status--active'
            case 'Completed':
                return 'sub-status--completed'
            case 'Client Breached Non Payment':
                return 'sub-status--breached'
            case 'Client Cancelled':
            case 'Merchant Rejected':
            case 'Expired':
            case 'Mutually Terminated':
                return 'sub-status--cancelled'
            default:
                return 'sub-status--default'
        }
    }

    // Derive subscription stats from subscriptionSummaries
    private getSubEntries(): any[] {
        if (!this.lead?.subscriptionSummaries) return []
        return Object.values(this.lead.subscriptionSummaries)
    }

    hasActiveSub(): boolean {
        return this.getSubEntries().some((s) => s.status === 'Active')
    }

    hasFutureSub(): boolean {
        const now = new Date()
        return this.getSubEntries().some((s) => {
            if (s.status !== 'Active') return false
            const start = s.startDate instanceof Date ? s.startDate : s.startDate?.seconds ? new Date(s.startDate.seconds * 1000) : null
            return start && start > now
        })
    }

    hasEverParked(): boolean {
        return this.getSubEntries().some((s) => s.status === 'Active' || s.status === 'Completed')
    }

    getCompletedCount(): number {
        return this.getSubEntries().filter((s) => s.status === 'Completed').length
    }

    getBreachedCount(): number {
        return this.getSubEntries().filter((s) => s.status === 'Client Breached Non Payment').length
    }

    onAddressInput(value: string): void {
        if (this.lead) {
            ;(this.lead as any).fullAddress = value
        }
    }

    getLeadTags(): string[] {
        return (this.lead as any)?.tags || []
    }
}
